import React from 'react'
import {Box, Grid, GridItem, useMultiStyleConfig} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {renderLayoutsSwitch, renderComponentsSwitch} from '../../utils/contentRenderer'
import {getHideStyles} from '../../utils/utils'

const DEFAULT_PADDING = {base: '32px 16px', md: '32px 16px', lg: '48px 24px'}

const LeftRightContentBlock = ({
    LeftColumn,
    RightColumn,
    DropDownAlignment,
    HideOnDevices,
    ...props
}) => {
    const hideStyles = getHideStyles(HideOnDevices)
    const styles = useMultiStyleConfig('LeftRightContentBlock', {...props})

    if (hideStyles.doNotLoadComponent) return null

    const isReversed = DropDownAlignment === 'Reverse (Right on Top)'

    const getPaddingStyles = (customPadding) => {
        const getValue = (customValue, defaultValue) => {
            // If custom value exists and is not empty string, use it
            if (customValue && customValue !== '') {
                return customValue
            }
            // Otherwise use default padding
            return defaultValue
        }

        const paddingValues = {
            base: getValue(customPadding?.PaddingMobile, DEFAULT_PADDING.base),
            md: getValue(customPadding?.PaddingTablet, DEFAULT_PADDING.md),
            lg: getValue(customPadding?.PaddingDesktop, DEFAULT_PADDING.lg)
        }

        return {padding: paddingValues}
    }

    const renderColumn = (column) => {
        if (!column) return null

        const {BackgroundColorPicker, Content, LeftRightContentBlockPaddingPerDevice} = column

        // Check if content contains only image/media blocks or full panel blocks
        const hasOnlyImageContent = Content?.every((item) => {
            const typename = item?.ContentLink?.Expanded?.__typename
            return (
                typename === 'ResponsiveMediaBlock' ||
                typename === 'ImageFile' ||
                typename === 'FullPanelBlock'
            )
        })

        // Only apply padding if content is not just images
        const paddingStyles = hasOnlyImageContent
            ? {}
            : getPaddingStyles(LeftRightContentBlockPaddingPerDevice)

        return (
            <Box
                backgroundColor={BackgroundColorPicker}
                {...paddingStyles}
                h="100%"
                w="100%"
                boxSizing="border-box"
                data-image-only={hasOnlyImageContent}
                sx={
                    hasOnlyImageContent
                        ? {
                              '& div': {
                                  height: '100%'
                              },
                              '& img': {
                                  height: '100%',
                                  objectFit: 'cover',
                                  objectPosition: 'center'
                              },
                              '& [data-block-type="CtaBlock"]': {
                                  height: 'auto',
                                  '& div': {
                                      height: 'auto'
                                  }
                              },
                              '& [data-block-type="CtaItemBlock"]': {
                                  height: 'auto',
                                  '& div': {
                                      height: 'auto'
                                  }
                              }
                          }
                        : {
                              '& img': {
                                  maxWidth: '100%',
                                  height: 'auto'
                              }
                          }
                }
            >
                {Content?.map((item, index) => {
                    const expandedContent = item?.ContentLink?.Expanded
                    if (!expandedContent) return null

                    // Pass customMediaStyling for image-only columns to enable cover behavior
                    const imageOnlyProps = hasOnlyImageContent
                        ? {customMediaStyling: {height: '100%', objectFit: 'cover'}}
                        : {}

                    return (
                        <React.Fragment
                            key={`${expandedContent.__typename}-${
                                expandedContent.ContentLink?.Id || index
                            }`}
                        >
                            {renderLayoutsSwitch(expandedContent) ||
                                renderComponentsSwitch(expandedContent, imageOnlyProps)}
                        </React.Fragment>
                    )
                })}
            </Box>
        )
    }

    return (
        <Box data-block-type="LeftRightContentBlock" {...styles.container} {...hideStyles}>
            <Grid templateColumns={{base: '1fr', lg: '1fr 1fr'}} gap={0} {...styles.grid}>
                <GridItem {...styles.leftColumn} order={{base: isReversed ? 2 : 1, lg: 1}} minW={0}>
                    {renderColumn(LeftColumn)}
                </GridItem>
                <GridItem
                    {...styles.rightColumn}
                    order={{base: isReversed ? 1 : 2, lg: 2}}
                    minW={0}
                >
                    {renderColumn(RightColumn)}
                </GridItem>
            </Grid>
        </Box>
    )
}

const columnPropType = PropTypes.shape({
    BackgroundColorPicker: PropTypes.string,
    Content: PropTypes.arrayOf(
        PropTypes.shape({
            ContentLink: PropTypes.shape({
                Expanded: PropTypes.object
            })
        })
    ),
    LeftRightContentBlockPaddingPerDevice: PropTypes.shape({
        PaddingMobile: PropTypes.string,
        PaddingTablet: PropTypes.string,
        PaddingDesktop: PropTypes.string
    })
})

LeftRightContentBlock.propTypes = {
    LeftColumn: columnPropType,
    RightColumn: columnPropType,
    DropDownAlignment: PropTypes.string,
    HideOnDevices: PropTypes.string
}

export default LeftRightContentBlock
